import { CACHE_SPLIT, localToday } from './plan';
import type { Chat } from './plan';
import type { FileFormat } from './files';

export type TaskIntent = 'query' | 'aggregate' | 'rank' | 'list' | 'prepare' | 'export' | 'howto';

export type ArtifactSpec = {
  format: FileFormat;
  requiredColumns: string[];
};

/** 문서에 상세 표를 얼마나·그래프를 넣을지. 형식(artifact)과 따로 다닌다 — 「보고서 한장으로」에는 형식 말이 없다. */
export type DocumentLayout = {
  detail: 'none' | 'top' | 'all';
  charts: boolean;
  /**
   * 발표자료 꼴. keynote는 발표나 홍보용으로 시각 자료 위주(애플 키노트처럼)를 청했을 때다. 없거나 report면 보고용(한국형 보고서
   * 장표). PDF와 워드는 보지 않는다.
   */
  style?: 'report' | 'keynote';
};

const DETAILS = new Set(['none', 'top', 'all']);

/**
 * 이번 발화가 「이름에 숫자가 붙은 계정」(같은 사람을 관리상 두 번 등록한 계정, 연차 자료에서 계산 전에 뺀다)을 어떻게 다루는가.
 * none은 그 계정에 대한 말이 없음, ask는 그 계정을 묻기, include는 넣기(빼지 말기), exclude는 빼라고 함, unclear는 판정 못 함이다.
 */
export type DuplicateAction = 'none' | 'ask' | 'include' | 'exclude' | 'unclear';

const DUPLICATE_ACTIONS = new Set<string>(['none', 'ask', 'include', 'exclude', 'unclear']);

/**
 * 대화 문구가 아니라 사용자가 끝내려는 업무를 보존한다.
 *
 * capability는 화면 경로가 아닌 안정적인 업무 ID다. 화면과 DOM은 실행기가
 * 나중에 고르며, 후속 발화는 이 객체의 일부 슬롯만 갱신한다.
 */
export type TaskSpec = {
  intent: TaskIntent;
  capability: string;
  goal: string;
  entity?: string;
  metric?: string;
  dimensions: string[];
  operations: string[];
  filters: Record<string, string>;
  artifact?: ArtifactSpec;
  /**
   * 문서에 담을 분량. 요청을 읽는 이 단계가 정한다 — 바깥에서 행 수를 자르면
   * 「전체 목록 PDF로」까지 말없이 잘린다(2026-10-23).
   */
  layout?: DocumentLayout;
  unresolved: string[];
  /**
   * 지금까지 오간 것의 요지. 한두 줄.
   *
   * 대화가 길어지면 형식·기간·산출물을 각자 정규식으로 캐내는 땜질이 늘어난다.
   * 그 대신 **이어지는 한 덩이**를 들고 다닌다 — 매 턴 도는 이 갱신 호출이 같이
   * 쓰므로 모델 호출이 늘지 않는다.
   */
  summary?: string;
  /**
   * 이번 발화가 이름에 숫자가 붙은 계정을 어떻게 다루는가. 근거 구절(quote)을 먼저 적게 한다. 이번 발화의 값이라 앞 작업에서 잇지
   * 않는다. 정규식만으로는 부정(「빼지 말고」), 어순, 다른 대상 제외를 계속 놓쳤다(독립 재검토 네 차례). 코드가 구절을 발화와 맞대
   * 검사하고 정규식 판정과 맞대 본다(`index.ts`의 `duplicateDecision`).
   */
  duplicates?: { quote: string; action: DuplicateAction };
  /** 사용자가 빼라고 한 다른 집단(임원, 퇴사자). 기본 규칙의 예외가 아니라 그 위에 거는 사용자 조건이다. 이번 발화의 값이다. */
  exclusions?: { target: string; quote: string }[];
  /**
   * 발화가 가리키는 업무 용어(`kb/experts.json`의 용어 이름)와 그 구절. 다른 말로 불러도 고른다: 「자체점검 중대 이슈」가 별칭 글자
   * 맞추기에 안 걸려 1턴이 점검결과 화면으로 갔다(실제 테넌트, 2026-10-27). 용어는 목록에 있어야 하고 구절은 발화에 있어야 한다
   * (`withTerms`). 같은 업무에서 앞 턴에 고른 용어는 구절 없이 잇는다.
   */
  terms?: { term: string; quote: string }[];
  /** 사용자가 자료의 특징, 쏠림, 원인, 조사를 청한 구절. 분석 틀(`frameFor`)을 돌린다. 이번 발화의 값이다. */
  analysis?: { quote: string };
  /**
   * 사용자가 이름에 숫자가 붙은 계정을 넣으라고 해 둔 상태(실행기가 쓴다). 같은 업무 안에서 다음 턴에 잇고, 빼라고 하거나 업무가
   * 바뀌면 끈다.
   */
  keepNumbered?: boolean;
  /**
   * 지금 화면에 열려 있는 미저장 초안에 이미 넣은 값.
   *
   * 되묻기에 답한 다음 턴의 계획은 **방금 고른 값 하나만** 낸다. 그것만 넣으면 앞서
   * 넣었던 견적서명이 빈 채로 저장이 시도돼 `필수입력확인: 견적서명을 입력하십시요` 로
   * 막힌다 — 화면에는 값이 있는데 계획은 그것을 모른다. 다음 턴이 합칠 수 있게 남긴다.
   */
  /**
   * 사람이 말한 이름 → 화면이 확정한 이름.
   *
   * 사람은 `덕봉빌딩` 이라 하고 화면은 `덕봉빌딩(두암정철)` 을 요구한다. 한 번 골랐는데
   * 다음 턴에 또 물으면 같은 일을 두 번 시키는 것이다. 고른 그 순간을 기억한다.
   */
  aliases?: Record<string, string>;
  /**
   * 지금 되묻고 있는 칸과, 그 칸에서 못 고른 이름. 답이 오면 별칭이 된다.
   *
   * 내민 선택지도 함께 들고 있는다. 사용자가 누른 버튼의 글자가 곧 다음 턴의 발화인데,
   * 계획은 그 값을 어디에도 안 싣는 일이 흔하다 — 자재처럼 표에 실리는 값이 그렇다.
   * 그때는 **발화 자체**가 답이고, 내민 목록에 있는 글자일 때만 그렇게 읽는다.
   */
  pending?: {
    field: string;
    wanted: string;
    options?: string[];
    /**
     * 옵션의 라벨→코드. 표 셀 품목처럼 코드로 확정해야 하는 되묻기가 싣는다. 답 턴에
     * 사용자가 고른 라벨을 이 코드로 바꿔 **행에는 코드**를 넣는다 — 라벨은 팝업이
     * 못 되맞춰 같은 질문이 되돌아왔다. 없으면 예전처럼 라벨을 넣는다.
     */
    optionCodes?: { label: string; code: string }[];
  };
  draft?: {
    route: string;
    fields: Record<string, string>;
    /** 상세행도 같이 잃는다. 품목을 고르고 나면 계획은 행을 다시 내지 않는다. */
    rows?: { stateKey: string; items: Record<string, string>[]; tab?: string; addLabel?: string };
    /** 이미 있는 문서를 고치던 초안인가. 아니면 되묻기 답 턴이 고치기로 바뀌면 안 된다. */
    editing?: boolean;
  };
};

export const TASK_PROMPT = `너는 ERP 대화의 작업 상태 갱신기다.
이전 작업과 최신 사용자 발화를 합쳐 완전한 TaskSpec JSON 하나만 출력한다.

규칙:
- 최신 사용자의 명시적 수정이 이전 값을 덮어쓴다.
- 이 대화는 회사 ERP 안이다. 낯선 말은 일반 국어(문법·맞춤법)로 읽지 말고 ERP 업무 말의 오타·준말로 먼저 읽는다
  (시제→시재, 결제→결재, 근테→근태). "아니 회계"처럼 앞 말을 고쳐 주는 말은 앞 말과 합쳐 읽는다.
- 최신 발화가 "그대로", "다시", "기존 것"처럼 앞 작업을 가리키지 않고 그 자체로 완전한 새 요청이면, 이전 작업의 부가 지시·operations·filters를 관성적으로 남기지 않는다.
- 다만 **기간은 예외다.** 사람은 기간을 한 번만 말한다. "올해 매출 성장세" 다음의 "수금 상황은?" 도
  올해를 묻는 것이다. 최신 발화에 기간을 가리키는 말이 없으면 이전 기간을 그대로 유지하고,
  그것을 unresolved 에 넣어 되묻지 마라.
- "엑셀로", "그걸 PDF로" 같은 말은 이전 업무를 버리지 않고 artifact만 바꾼다.
- "발표자료"·"PPT"·"슬라이드"는 artifact.format 을 pptx 로 적는다.
- capability는 화면 경로가 아니라 안정적인 업무 ID다. 예: hr.approved_leave, sales.revenue, employee.remodeling_performance.
- 아래 허용 capability 중 의미가 맞는 것이 있으면 반드시 그 ID를 그대로 쓴다.
- capability 설명에 본인 과거 ERP 기록을 조회·재사용할 수 있다고 적혀 있으면, 그 값이 현재 대화에 없다는 이유로 unresolved에 넣지 않는다. 실행기가 ERP에서 읽는다.
- 발화의 [민감값01] 같은 자리표는 **값이 있는 것이다.** 가려 둔 것뿐이니 unresolved 에
  넣어 되묻지 마라 — 실행기가 화면에 넣기 직전에 되돌린다.
- 답을 실질적으로 바꾸는 정보만 unresolved에 넣는다. 이미 선택한 기간이나 기준을 다시 묻지 않는다.
- intent가 prepare면 unresolved를 비운다. 초안은 화면을 연 뒤 채우고, 화면의 선택 칸이나
  현장·담당자 같은 조회 항목은 실행기가 화면에 있는 실제 후보를 읽어 되묻는다. 여기서 미리
  물으면 그 후보를 볼 기회를 잃는다. 발화에 없는 값은 비운 채로 두고 실행기에 넘긴다.
- "남은 것이 있는", "잔여가 있는", "미사용이 있는"은 filters에 "0 초과"로 적는다. 1 이상으로 좁히지 않는다.
  반일(0.5)도 남은 것이다. 「1 이상」으로 적은 판이 0.5만 남은 직원을 연차 촉진 대상에서 뺐다(실측 2026-10-26).
- 목록·순위·엑셀 요청은 결과에 필요한 열을 artifact.requiredColumns에 적는다.
  **기간은 열이 아니라 조건이다.** "이번 분기 매출을 현장별로" 의 열은 현장과 매출금액이고,
  기간은 filters 에 적는다. 기간을 열로 적어 두면 있지도 않은 열을 찾다가
  "기간 항목은 이 자료에 없습니다" 라고 답하게 된다. 달마다 한 줄인 표를 원할 때만
  그때는 열이다.
- 보고서·문서를 만들 때 layout 을 적는다: "한 장"·"요약본"이면 detail 은 top, "전체 목록"·"다 넣어"면 all,
  표가 필요 없다고 하면 none. "그래프·차트·시각화·한눈에"면 charts 는 true. 말이 없으면 생략한다(앞 값이 이어진다).
- 발표자료의 꼴은 layout.style에 적는다. 발표나 홍보용으로 글보다 시각 자료 위주를 청했으면(「키노트처럼」, 「발표용으로 임팩트
  있게」, 「글 줄이고 큰 숫자로」) keynote, 보고용을 청했으면 report. 꼴을 말하지 않았으면 style을 적지 않는다(앞 값이 이어진다).
  「이쁘게」만으로는 keynote로 바꾸지 않는다. 꼴을 말했으면 분량 말이 없어도 layout을 적는다. 이전 작업에 layout이 있으면 detail과
  charts는 그대로 두고, 없으면 detail은 top, charts는 true다.
  예: 「홍보 자리에서 쓸 거라 글은 빼고 큰 숫자 위주 슬라이드로」 → "layout":{"detail":"top","charts":true,"style":"keynote"}
- artifact 는 **생략하면 이전 값이 그대로 이어진다.** 앞서 워드를 요청했으면 이어지는 턴에서
  생략해도 워드로 남는다. 사용자가 파일을 그만두라고 했을 때만 null 로 적는다.
  파일을 처음부터 요청하지 않은 대화에서는 생략한다. 빈 객체를 만들지 않는다.
- summary 에는 **지금까지 오간 것의 요지**를 한두 줄로 적는다. 사용자가 무엇을 물었고,
  무엇을 이미 답했고, 무엇이 남았는지. 다음 턴은 이 글만 보고도 이어갈 수 있어야 한다.
  사무직 직원이 읽는 말로 쓰고, 화면 경로나 state key 는 넣지 않는다.
- 추측으로 화면 경로, state key, DB 컬럼명을 만들지 않는다.
- goal 은 답변에까지 흘러 사무직 직원이 읽는다. **여기 쓰인 우리 쪽 말을 goal 에 옮기지 마라** —
  capability, 허용 목록, state key 같은 말은 그 사람에게 아무 뜻이 없다.
  맞는 업무가 없으면 goal 에는 사용자가 물은 것을 그대로 적고, 판단은 실행기가 한다.
- duplicates에는 이번 발화가 「이름에 숫자가 붙은 계정」(같은 사람을 관리상 두 번 등록한 계정. 연차 자료에서는 계산 전에 뺀다)을
  어떻게 다루는지 적는다. quote에 그 계정을 가리킨 구절을 발화 그대로 먼저 옮기고 action을 고른다. 그 계정에 대한 말이 없으면
  none(quote는 빈 글자), 그 계정을 묻기만 하면 ask, 넣어 달라거나 빼지 말라고 하면 include, 빼라고 하면 exclude, 판단이 서지
  않으면 unclear. 직전 답에서 뺀 계정을 가리키는 말(「그 계정들」, 「아까 뺀 사람들」)도 그 계정이다. 「뺀 계정 보여줘」(빠진 그
  계정을 보자는 말)는 ask, 「계정 뺀 명단」(그 계정을 뺀 명단)은 exclude다.
- 다른 집단을 빼라는 말(임원, 퇴사자, 특정 부서)은 그 계정이 아니다. duplicates는 none으로 두고 exclusions에 대상과 구절을
  적는다. 그런 말이 없으면 exclusions는 생략한다.
  예: 「숫자 붙은 계정도 빼지 말고 다시 보여줘」 → "duplicates":{"quote":"숫자 붙은 계정도 빼지 말고","action":"include"}
  「숫자 붙은 계정 뺀 명단 엑셀로」 → "duplicates":{"quote":"숫자 붙은 계정 뺀","action":"exclude"}
  「아까 뺀 계정 누구야?」 → "duplicates":{"quote":"아까 뺀 계정","action":"ask"}
  「임원 제외하고 잔여 명단 뽑아줘」 → "duplicates":{"quote":"","action":"none"}, "exclusions":[{"target":"임원","quote":"임원 제외하고"}]
  「연말까지 쓰라고 공지 만들어줘」, 「번호 붙여서 정리해줘」, 「연차 안 쓴 직원 보여줘」 → "duplicates":{"quote":"","action":"none"}
- terms에는 발화가 가리키는 업무 용어를 아래 업무 용어 목록에서 골라 적는다. 다른 말로 불러도 같은 것을 가리키면 고른다. quote에는
  그것을 가리킨 구절을 발화 그대로 옮긴다. 목록에 없는 말은 적지 않는다. 같은 업무가 이어지면 이전 작업의 terms를 그대로 둔다.
  goal에는 고른 용어 이름을 쓴다.
- analysis에는 사용자가 자료의 특징, 쏠림, 원인을 보거나 조사, 분석, 보고 준비를 청한 구절을 발화 그대로 적는다. 그런 말이 없으면
  생략한다.
  예: 「이번 달 회사 전체 남은 돈 흐름 좀 조사해줘」 → "terms":[{"term":"시재","quote":"회사 전체 남은 돈"}],
  "analysis":{"quote":"조사해줘"}
  「이번 달 중대결함 몇 건이야?」 → "terms":[{"term":"중대결함","quote":"중대결함"}] (analysis는 생략)

스키마:
{"intent":"query|aggregate|rank|list|prepare|export|howto","capability":"domain.action","goal":"완전한 한국어 목표","entity":"선택","metric":"선택","dimensions":[],"operations":[],"filters":{},"artifact":{"format":"xlsx|docx|pdf|pptx","requiredColumns":[]},"layout":{"detail":"none|top|all","charts":false,"style":"report|keynote"},"unresolved":[],"summary":"지금까지의 요지 한두 줄","duplicates":{"quote":"","action":"none|ask|include|exclude|unclear"},"exclusions":[{"target":"","quote":""}],"terms":[{"term":"","quote":""}],"analysis":{"quote":""}}

허용 capability:
{capabilities}

업무 용어 목록:
{terms}
${CACHE_SPLIT}
이전 작업:
{previous}

직전에 우리가 한 답(요지):
{lastAnswer}
{dropped}
오늘 날짜: {today}
「어제」「오늘」「내일」「이번 주」 같은 상대 날짜를 goal·filters 에 날짜로 적을 때는 **반드시 이 날 기준으로** 바꾼다.
자신이 없으면 날짜로 바꾸지 말고 사람이 한 말 그대로 둔다 — 다음 단계가 이 날 기준으로 바꾼다.

최신 사용자 발화:
{question}`;

const INTENTS = new Set<TaskIntent>(['query', 'aggregate', 'rank', 'list', 'prepare', 'export', 'howto']);
const FORMATS = new Set<FileFormat>(['xlsx', 'docx', 'pdf', 'pptx']);

function text(value: unknown, field: string, optional = false): string | undefined {
  if (optional && (value === undefined || value === null || value === '')) return undefined;
  if (typeof value !== 'string' || !value.trim()) throw new Error(`TaskSpec.${field}는 문자열이어야 한다`);
  return value.trim();
}

function scalar(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
}

function strings(value: unknown, field: string): string[] {
  // JSON 스키마 의미가 분명한 단일 항목은 모델의 흔한 축약 출력으로 받아들인다.
  if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
  if (!Array.isArray(value)) throw new Error(`TaskSpec.${field}는 문자열 배열이어야 한다`);
  // dimensions·operations·unresolved는 사람이 읽을 힌트다. 모델이 항목 하나를 객체로
  // 내는 일 때문에 질문 전체를 실패시킬 값어치가 없다 — 쓸 수 있는 것만 남긴다.
  return value.map(scalar).filter((item): item is string => Boolean(item));
}

/** 한 문장이라도 되는가. 낱말 하나나 숫자는 요지로 치지 않는다. */
function usableSummary(value: string | undefined): string | undefined {
  const text = value?.trim();
  if (!text || text.length < 8) return undefined;
  return text;
}

/** 모델이 실제로 말한 슬롯만. 비운 값은 "안 바뀜"으로 보고 이전 값을 남긴다. */
function stated(parsed: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(parsed)
    .filter(([, item]) => item !== undefined && item !== null && item !== ''));
}

/**
 * 모델 응답을 TaskSpec으로 읽는다.
 *
 * 되묻기가 오간 뒤 모델은 종종 바뀐 슬롯만 낸다. 후속 발화의 갱신이라는 뜻이므로
 * 이전 작업 위에 덮어 읽는다 — 안 바뀐 capability가 빠졌다고 대화를 끊지 않는다.
 */
export function parseTaskSpec(raw: string, previous?: TaskSpec): TaskSpec {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1] ?? raw;
  let value: Record<string, unknown>;
  try {
    value = JSON.parse(fenced.trim()) as Record<string, unknown>;
  } catch {
    throw new Error('TaskSpec 응답이 JSON이 아니다');
  }
  // 이번 발화의 값(duplicates, exclusions)은 모델이 이번에 낸 것만 읽는다. 앞 작업에서 이으면 앞 턴의 구절이 남는다.
  const own = value;
  if (previous) {
    value = { ...stated(previous as unknown as Record<string, unknown>), ...stated(value) };
  }
  const intent = text(value.intent, 'intent') as TaskIntent;
  if (!INTENTS.has(intent)) throw new Error(`지원하지 않는 TaskSpec.intent다: ${intent}`);
  const filters = value.filters;
  if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
    throw new Error('TaskSpec.filters는 문자열 값 객체여야 한다');
  }
  // LLM의 JSON 숫자·불리언은 의미가 분명한 스칼라이므로 화면 조건용 문자열로 맞춘다.
  // 값을 배열로 내는 일도 잦다("시간":["09:00","18:00"]) — 한 줄로 합친다.
  // 중첩 객체는 화면 조건으로 옮길 수 없으므로 버린다. 조건 하나 때문에 질문 전체를
  // 실패시키지 않는다 — 빠진 조건은 실행기가 되묻는다.
  const normalizedFilters: Record<string, string> = {};
  for (const [key, item] of Object.entries(filters as Record<string, unknown>)) {
    if (item === undefined || item === null || item === '') continue;
    const value = Array.isArray(item)
      ? item.map(scalar).filter(Boolean).join(', ')
      : scalar(item);
    if (value) normalizedFilters[key] = value;
  }
  let artifact: ArtifactSpec | undefined;
  if (value.artifact !== undefined && value.artifact !== null) {
    if (typeof value.artifact !== 'object' || Array.isArray(value.artifact)) {
      throw new Error('TaskSpec.artifact는 객체여야 한다');
    }
    const candidate = value.artifact as Record<string, unknown>;
    const emptyFormat = candidate.format === undefined || candidate.format === null
      || candidate.format === '';
    const emptyColumns = candidate.requiredColumns === undefined
      || (Array.isArray(candidate.requiredColumns) && candidate.requiredColumns.length === 0);
    // 파일을 요청하지 않은 발화에서 모델이 종종 artifact:{} 또는
    // {format:null, requiredColumns:[]}를 낸다. 의미상 "산출물 없음"이므로 정규화한다.
    // 다만 **앞서 파일을 요청했으면 그것을 지우지 않는다** — `그대로 진행해줘` 같은
    // 보충 발화에도 모델은 빈 artifact 를 낸다. `stated()` 는 빈 객체를 못 걸러
    // 이전 형식을 덮어썼고, 그 턴의 답변에는 파일 버튼이 붙지 않았다.
    if (emptyFormat && emptyColumns && intent !== 'export') {
      artifact = previous?.artifact;
    } else if (emptyFormat && intent !== 'export') {
      // **형식 없이 열만 낸 산출물**은 파일 요청이 아니다 — 모델이 답에 쓸 열을 여기 적곤 한다. 그것 하나로 과제를
      // 통째로 버렸다(「이번 달 고장접수 목록 보여줘」, 2026-10-15). 앞서 파일을 요청했으면 그것을 남긴다.
      artifact = previous?.artifact;
    } else {
      const format = text(candidate.format, 'artifact.format') as FileFormat;
      if (!FORMATS.has(format)) throw new Error(`지원하지 않는 artifact 형식이다: ${format}`);
      artifact = { format, requiredColumns: strings(candidate.requiredColumns ?? [], 'artifact.requiredColumns') };
    }
  }
  // 초안 작업인가. intent 하나에 기대면 흔들린다 — 같은 발화를 `prepare` 로도
  // `query` 로도 뽑는 턴을 봤고, 그때마다 화면에만 있는 값을 사람에게 되물었다.
  // 목표에 `초안` 이라고 적혀 있으면 그것은 화면을 열어 채우는 일이다.
  const goalText = text(value.goal, 'goal')!;
  const drafting = intent === 'prepare' || /초안/.test(goalText);
  const capability = text(value.capability, 'capability', true) ?? '';
  const exclusions = exclusionsOf(own.exclusions);
  const duplicates = duplicatesOf(own.duplicates);
  // 용어는 이번에 낸 것을 읽고, 칸을 아예 안 냈으면 같은 업무일 때만 앞 작업의 것을 잇는다. 검사는 `withTerms`가 한다. 업무가
  // 바뀌었는데 이으면 앞 주제의 화면이 계획 후보 첫 자리를 차지한다(독립 검토).
  const sameWork = !!previous && (previous.capability ?? '') === capability;
  const terms = own.terms === undefined ? (sameWork ? previous?.terms ?? [] : []) : termsOf(own.terms);
  const analysis = analysisOf(own.analysis);

  return {
    intent,
    // **맞는 업무가 없으면 모델은 `null` 을 낸다.** 그것 하나로 과제를 통째로 버렸다 — 「이번 달 견적 몇 건이야?」가
    // 세 번 중 두 번 과제 없이 다듬지 않은 말로 계획됐다(2026-10-15). 업무 없음은 빈 문자열이다.
    capability,
    goal: goalText,
    entity: text(value.entity, 'entity', true),
    metric: text(value.metric, 'metric', true),
    dimensions: strings(value.dimensions ?? [], 'dimensions'),
    operations: strings(value.operations ?? [], 'operations'),
    filters: normalizedFilters,
    artifact,
    layout: withStyle(layoutOf(value.layout), own.layout, previous?.layout),
    // 초안은 되묻지 않고 화면을 먼저 연다. 화면의 선택 칸과 검색 팝업은 실행기가
    // 실제 후보를 읽어 버튼으로 내민다 — 여기서 미리 물으면 그 후보를 볼 기회가 없고,
    // 사용자는 화면에만 있는 값(`출장분류`)을 맞혀야 한다. 프롬프트로 적어 두었지만
    // 지켜지지 않는 턴이 있어 코드에서 막는다.
    unresolved: drafting ? [] : strings(value.unresolved ?? [], 'unresolved'),
    // 화면에 열려 있는 초안에 무엇을 넣었는지는 모델이 아니라 실행기가 안다.
    // 갱신 호출이 이것을 지우면 되묻기 다음 턴이 앞 값을 다시 잃는다.
    ...(value.draft ? { draft: value.draft as TaskSpec['draft'] } : {}),
    // 별칭도 실행기가 채운다. 갱신 호출이 지우면 다음 턴이 또 묻는다.
    ...(value.aliases ? { aliases: value.aliases as Record<string, string> } : {}),
    ...(value.pending ? { pending: value.pending as TaskSpec['pending'] } : {}),
    // 요지는 있으면 좋고 없으면 그만이다. 이것 하나 때문에 질문을 실패시키지 않는다.
    // 다만 **너무 짧은 것은 요지가 아니다** — 모델이 `2` 를 적어 보낸 턴이 있었고,
    // 그것이 계획 프롬프트에 `지금까지: 2` 로 실렸다. 잡음은 없느니만 못하다.
    summary: usableSummary(text(value.summary, 'summary', true)),
    ...(duplicates ? { duplicates } : {}),
    ...(exclusions.length ? { exclusions } : {}),
    ...(terms.length ? { terms } : {}),
    ...(analysis ? { analysis } : {}),
    // 넣기 상태는 실행기가 정한다. 같은 업무면 갱신을 거쳐도 남긴다. 되묻기·안내처럼 일찍 끝나는 턴의 과제가 다음 턴 맥락이 되어
    // 상태를 잃었다(독립 검토). 빼라고 한 턴은 잇지 않는다(독립 재검토).
    ...(previous?.keepNumbered && (previous.capability ?? '') === capability && duplicates?.action !== 'exclude'
      ? { keepNumbered: true } : {}),
  };
}

/** 사람 집단이 아닌 문서 요소. 「그래프 빼고」는 다른 집단 제외가 아니다(독립 재검토). */
const DOCUMENT_PART = /^(?:그래프|차트|표|그림|사진|페이지|목차|표지|제목|서명|로고|요약)$/;

/** 빼라는 말과 빼지 말라는 말. */
const DROP_SAID = /빼|뺀|제외|제거|말고|없이|빠지|빠진|거르|걸러|거른/;
const NOT_DROP = /(?:빼|제외하|지우|삭제하)지\s*(?:말|마)/;

/**
 * 다른 집단 제외를 검사해 처리(operations)에도 「○○ 제외」로 싣는다. 구절이 발화에 있거나, 대상 뒤 같은 문장에 빼라는 말이 있어야
 * 받는다(지어낸 조건이 자료를 좁히지 않게). 빼지 말라는 말은 아니다. 구절만 보면 「임원은 빼고」를 「임원 빼고」로 옮긴 제외가 처리에서도
 * 사라져 계획이 임원을 넣었고(독립 재검토), 발화 어디든 빼기 말만 보면 다른 문장의 「빼고」로 지어낸 대상이 들어왔다(독립 재검토 네
 * 번째). 문서 요소는 받지 않는다. exclusions에만 있으면 같은 일 판정이 못 보고 앞 결과(임원 포함)로 공지를 만들었고, 다음 턴에는
 * 조건이 사라졌다(독립 검토). 처리는 이전 작업으로 다음 턴에 이어진다.
 */
function withExclusions(task: TaskSpec, question: string): TaskSpec {
  const flat = (text: string) => text.replace(/[\s.,!?·「」"'()]+/g, '');
  // 줄바꿈은 남긴다. 문장을 가르는 자리다.
  const squeezed = question.replace(/[ \t]+/g, '');
  // 대상이 나온 자리마다 그 뒤 같은 문장(「임원 보고용으로 정리해줘. 임원은 빼고」의 둘째 「임원」).
  const clauses = (target: string) => {
    const key = target.replace(/\s+/g, '');
    const found: string[] = [];
    for (let at = key ? squeezed.indexOf(key) : -1; at >= 0; at = squeezed.indexOf(key, at + 1)) {
      found.push(squeezed.slice(at + key.length).split(/[.?!\n]/)[0] ?? '');
    }
    return found;
  };
  const said = (one: { target: string; quote: string }) => (!!flat(one.quote) && flat(question).includes(flat(one.quote))
    && !NOT_DROP.test(one.quote)) || clauses(one.target).some((clause) => DROP_SAID.test(clause) && !NOT_DROP.test(clause));
  const exclusions = (task.exclusions ?? []).filter((one) => said(one) && !DOCUMENT_PART.test(one.target));
  const { exclusions: _said, ...rest } = task;
  return {
    ...rest,
    operations: [...task.operations, ...exclusions
      .filter((one) => !task.operations.some((operation) => operation.includes(one.target))).map((one) => `${one.target} 제외`)],
    ...(exclusions.length ? { exclusions } : {}),
  };
}

/**
 * 고른 용어를 검사한다. 용어는 목록에 있어야 하고, 구절이 발화에 있거나(공백과 부호 무시) 앞 작업이 이미 고른 용어여야 한다.
 * 분석 요청은 구절이 이번 발화에 있어야 한다. 지어낸 용어가 다른 화면으로 계획을 끌고 가지 않게 한다.
 */
export function withTerms(task: TaskSpec, question: string, index: string[], previous?: TaskSpec): TaskSpec {
  const flat = (text: string) => text.replace(/[\s.,!?·「」"'()]+/g, '');
  // 한 글자 구절(「결」)은 어느 질문에나 있다(독립 검토).
  const heard = (quote: string) => flat(quote).length >= 2 && flat(question).includes(flat(quote));
  const known = new Set(index);
  // 구절 없이 잇는 것은 같은 업무의 앞 작업이 고른 용어뿐이다.
  const carried = previous && (previous.capability ?? '') === (task.capability ?? '') ? previous.terms ?? [] : [];
  const terms = (task.terms ?? []).filter((one, at, all) => known.has(one.term)
    && all.findIndex((other) => other.term === one.term) === at
    && (heard(one.quote) || carried.some((before) => before.term === one.term)));
  const { terms: _terms, analysis: _analysis, ...rest } = task;
  return {
    ...rest,
    ...(terms.length ? { terms } : {}),
    ...(task.analysis && heard(task.analysis.quote) ? { analysis: task.analysis } : {}),
  };
}

/** 앞 턴 과제에서 이번 발화의 값(duplicates, exclusions, analysis)을 뗀다. 과제 단계가 실패하거나 없을 때 앞 턴의 구절로 판정하지 않게(독립 검토). */
export function carriedTask(task: TaskSpec): TaskSpec {
  const { duplicates: _duplicates, exclusions: _exclusions, analysis: _analysis, ...rest } = task;
  return rest;
}

/** 용어 이름이 있는 항목만. */
function termsOf(value: unknown): { term: string; quote: string }[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const { term, quote } = item as Record<string, unknown>;
    return typeof term === 'string' && term.trim() ? [{ term: term.trim(), quote: typeof quote === 'string' ? quote.trim() : '' }] : [];
  });
}

/** 구절이 있는 분석 요청만. */
function analysisOf(value: unknown): TaskSpec['analysis'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const quote = (value as Record<string, unknown>).quote;
  return typeof quote === 'string' && quote.trim() ? { quote: quote.trim() } : undefined;
}

/** 모르는 처리 값은 받지 않는다(값이 없는 것과 같다). 대소문자는 맞춰 읽는다(벤더가 enum 대소문자를 보장하지 않는다). */
function duplicatesOf(value: unknown): TaskSpec['duplicates'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const candidate = value as Record<string, unknown>;
  const action = typeof candidate.action === 'string' ? candidate.action.trim().toLowerCase() : '';
  if (!DUPLICATE_ACTIONS.has(action)) return undefined;
  return { quote: typeof candidate.quote === 'string' ? candidate.quote.trim() : '', action: action as DuplicateAction };
}

/** 대상이 있는 항목만 받는다. */
function exclusionsOf(value: unknown): { target: string; quote: string }[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const { target, quote } = item as Record<string, unknown>;
    return typeof target === 'string' && target.trim() ? [{ target: target.trim(), quote: typeof quote === 'string' ? quote.trim() : '' }] : [];
  });
}

/** 이전 작업으로 프롬프트에 싣는 값. 이번 발화의 값과 실행기 상태는 뺀다. */
function previousForPrompt(previous: TaskSpec): Omit<TaskSpec, 'duplicates' | 'exclusions' | 'keepNumbered' | 'analysis'> {
  const { duplicates: _duplicates, exclusions: _exclusions, keepNumbered: _keep, analysis: _analysis, ...rest } = previous;
  return rest;
}

/**
 * 발표자료 꼴은 말하지 않으면 앞 값을 잇는다. 모델이 이번에 낸 layout에 style 칸이 없으면 앞 작업의 style을 붙인다. 「표는 빼줘」 같은
 * 다음 턴이 분량만 고쳐 적어 마케팅형이 보고용으로 돌아갔다(독립 검토). style을 적었으면(report 포함) 그대로다.
 */
function withStyle(layout: DocumentLayout | undefined, own: unknown, previous: DocumentLayout | undefined): DocumentLayout | undefined {
  if (!layout || layout.style || !previous?.style) return layout;
  const stated = !!own && typeof own === 'object' && !Array.isArray(own) && 'style' in (own as Record<string, unknown>);
  return stated ? layout : { ...layout, style: previous.style };
}

/** 모르는 분량은 버린다 — 틀린 값으로 표를 자르느니 지금처럼 전부 넣는다. */
function layoutOf(value: unknown): DocumentLayout | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.detail !== 'string' || !DETAILS.has(candidate.detail)) return undefined;
  // 모르는 꼴은 보고용으로 본다(값이 없는 것과 같다).
  const style = typeof candidate.style === 'string' && candidate.style.trim().toLowerCase() === 'keynote' ? 'keynote' as const : undefined;
  return { detail: candidate.detail as DocumentLayout['detail'], charts: candidate.charts === true, ...(style ? { style } : {}) };
}

/** 기간을 가리키는 조건인가. 키로도 값으로도 알아본다. */
export function periodish(key: string, value: string): boolean {
  return /기간|일자|날짜|년|연도|월|분기|from|to|start|end|date/i.test(key)
    || /^\d{4}(-\d{2}){0,2}$/.test(value.trim());
}

/**
 * 이미 아는 기간을 다시 묻지 않는다.
 *
 * `올해 매출 성장세` 다음의 `수금 상태는 어때?` 가 `조회할 기간을 알려 주세요` 로
 * 끝났다. 사람은 기간을 한 번만 말하고, 그 기간은 바로 앞 작업에 남아 있다.
 * 프롬프트에 적어 두었지만 지키는 턴과 아닌 턴이 갈렸다 — 모델이 지켜 주기를 바라는
 * 규칙은 코드로도 걸어 둔다.
 */
function withKnownPeriod(task: TaskSpec, previous: TaskSpec | undefined): TaskSpec {
  // 기간을 **되물으려 할 때만** 물려준다. 그러지 않으면 새 발화가 조건을 갈아 끼운
  // 자리에까지 옛 기간이 끼어든다 — 후속 발화는 앞 작업을 덮어쓰는 것이 맞다.
  const asksPeriod = task.unresolved.some((item) => periodish(item, ''));
  if (!asksPeriod) return task;
  const known = Object.entries(previous?.filters ?? {}).filter(([key, value]) => periodish(key, value));
  const unresolved = task.unresolved.filter((item) => !periodish(item, ''));
  if (!known.length) return task;
  const mine = Object.entries(task.filters).some(([key, value]) => periodish(key, value));
  const filters = mine ? task.filters : { ...Object.fromEntries(known), ...task.filters };
  return { ...task, filters, unresolved };
}

export async function updateTaskSpec(
  question: string,
  previous: TaskSpec | undefined,
  chat: Chat,
  capabilities: { id: string; description: string }[] = [],
  lastAnswer?: string,
  now: Date = new Date(),
  /** 질문에 든 업무 용어의 뜻(`glossaryFor`). capability 를 고를 때 헷갈리는 말을 가른다. */
  glossary = '',
  /** 직전 답에서 기본 규칙으로 뺀 계정 이름. 「그 사람들」, 「아까 빠진 두 명」이 누구인지 과제 단계가 알게 한다(독립 검토). */
  dropped: string[] = [],
  /** 업무 용어 이름 목록(`kb/experts.json`). 과제가 다른 말로 부른 용어를 여기서 고른다. */
  termIndex: string[] = [],
): Promise<TaskSpec> {
  // **이 단계도 오늘을 안다.** 계획 단계에만 오늘이 있어, 「어제 들어온 입금」이 10/7 에 「10월 5일」로
  // 바뀐 채 계획으로 넘어갔다 — 계획의 「어제·내일」 검사는 사람 말이 이미 날짜로 바뀐 뒤라 못 잡았다(2026-10-07).
  const today = `${localToday(now)} ${'일월화수목금토'[now.getDay()]}요일`;
  const prompt = TASK_PROMPT
    .replace('{today}', today)
    .replace('{capabilities}', capabilities.length
      ? capabilities.map((capability) => `${capability.id}: ${capability.description}`).join('\n')
      : '등록된 capability 없음')
    .replace('{terms}', termIndex.length ? termIndex.join(', ') : '없음')
    // 앞 턴의 duplicates 구절이 남아 있으면 모델이 그대로 옮겨 적는다. 그 구절은 이번 발화에 없어 판정이 흔들린다.
    .replace('{previous}', previous ? JSON.stringify(previousForPrompt(previous)) : '없음')
    // 답 전문을 넣으면 프롬프트가 답변만큼 커진다. 요지를 만드는 데는 앞부분이면 된다.
    .replace('{lastAnswer}', lastAnswer?.replace(/\s+/g, ' ').slice(0, 400) || '없음')
    // 이름만 준다. 뺀 계정 문장을 주면 과제가 그것을 조건으로 적어 다음 턴이 같은 일로 보지 않았다(독립 검토).
    .replace('{dropped}', dropped.length
      ? `\n직전 답에서 뺀 계정: ${dropped.join(', ')} (이름에 숫자가 붙은 계정이라 기본 규칙으로 이미 뺐다. filters, operations에 적지 않는다.`
        + ' 사용자가 이 계정을 가리키면 duplicates에 적는다)\n'
      : '')
    .replace('{question}', question)
    + (glossary ? `\n\n${glossary}` : '');
  const parsed = withTerms(withExclusions(withKnownPeriod(parseTaskSpec(await chat(prompt, { step: 'task' }), previous), previous), question),
    question, termIndex, previous);
  return draftRequest(question, parsed) ? { ...parsed, unresolved: [] } : parsed;
}

/**
 * 화면에 초안을 채워 달라는 말인가.
 *
 * intent 하나로는 못 가른다 — `가지급 신청 초안 잡아줘` 를 `query` 로 뽑고 계정과목·
 * 지급유형·지급은행을 되물은 턴이 있었다. 셋 다 화면을 열면 목록으로 보이는 것이다.
 * 파일을 만들어 달라는 말(`엑셀로`, `PDF 로`)은 산출물이 있으니 여기 걸리지 않는다.
 */
function draftRequest(question: string, task: TaskSpec): boolean {
  if (task.intent === 'prepare' || /초안/.test(task.goal)) return true;
  if (task.artifact) return false;
  return /(써|작성해|만들어|올려|잡아|넣어|채워)\s*(줘|주세요|줄래|다오)/.test(question);
}

/** 계획·계산 모델이 짧은 후속 발화 대신 완전한 목표를 보게 한다. */
export function taskQuestion(task: TaskSpec): string {
  const parts = [task.goal];
  // 계획과 계산은 이 문장만 본다. 대화가 어디까지 왔는지를 여기에 실어 준다.
  if (task.summary) parts.push(`지금까지: ${task.summary}`);
  const filters = Object.entries(task.filters).map(([key, value]) => `${key}=${value}`);
  if (filters.length) parts.push(`조건: ${filters.join(', ')}`);
  if (task.dimensions.length) parts.push(`구분: ${task.dimensions.join(', ')}`);
  if (task.operations.length) parts.push(`처리: ${task.operations.join(', ')}`);
  if (task.artifact) {
    parts.push(`산출물: ${task.artifact.format}`);
    if (task.artifact.requiredColumns.length) {
      parts.push(`산출물 필수 열: ${task.artifact.requiredColumns.join(', ')}`);
    }
  }
  if (task.layout) parts.push(`문서: 표 ${task.layout.detail}, 그래프 ${task.layout.charts ? '있음' : '없음'}`);
  return parts.join('\n');
}

export function taskNeedsTable(task: TaskSpec): boolean {
  return task.intent === 'list' || task.intent === 'rank'
    || task.artifact?.format === 'xlsx'
    || Boolean(task.artifact?.requiredColumns.length);
}
